import type { Patch } from "../engine/types";
import { clonePatch } from "../engine/defaultPatch";
import type { Palette, PaletteSlot, PatchSlot } from "./palette";
import { colorFor, genId, savePalette } from "./palette";
import { makeDraggable, registerDropZone } from "./dragDrop";

// The clipboard canvas: slots laid out as groups of 4 ("chains"), chains
// stacked, with a band running between groups + chains. The band doubles as a
// finger-scroll anchor (grab it to pan the canvas without touching a chip).
const VISIBLE = 64;
const GROUP_SIZE = 4;        // chips per group
const GROUPS_PER_CHAIN = 4;  // groups per chain (row) → 16 chips wide

export interface PaletteViewOptions {
  /** The live palette array (mutated in place). */
  palette: Palette;
  /** The host's current working sound + the note last played, for capture. */
  captureCurrent: () => { patch: Patch; lastMidi: number };
  /** Audition a slot (click, or SPACE on the selected slot). */
  onPreview: (slot: PaletteSlot) => void;
  /** Load a sound back into the editor (double-click). */
  onLoad: (patch: Patch) => void;
  /** Called immediately BEFORE anything destructive, so the host can snapshot
   *  for undo. `label` describes the action, e.g. `Delete "Kick"`. */
  beforeChange?: (label: string) => void;
}

function shortLabel(name: string): string {
  return (name || "snd").slice(0, 6);
}

function band(cls: string): HTMLElement {
  const b = document.createElement("div");
  b.className = `pal-band ${cls}`;
  return b;
}

// Floating left-edge clipboard of sounds. Single click = select + preview,
// double click = load (synth sounds), hover ✕ = delete, drag a tile = move/swap.
// A slot holds either a synth patch or a recorded sample.
export class PaletteView {
  private readonly palette: Palette;
  selectedId: string | null = null;
  // Drop zones are re-registered every render(); track their teardown fns.
  private zoneCleanups: Array<() => void> = [];

  constructor(
    private readonly container: HTMLElement,
    private readonly opts: PaletteViewOptions,
  ) {
    this.palette = opts.palette;
    this.render();
    this.setupPan();
  }

  getSelected(): PaletteSlot | null {
    return this.palette.find((s) => s?.id === this.selectedId) ?? null;
  }

  render(): void {
    this.container.classList.add("palette");
    for (const teardown of this.zoneCleanups) teardown();
    this.zoneCleanups = [];
    this.container.innerHTML = "";

    // 2D canvas: groups of 4 chips, separated by a band; chains (rows) stacked,
    // separated by a band. The band is the scroll handle (see setupPan).
    const canvas = document.createElement("div");
    canvas.className = "palette-canvas";
    const perChain = GROUP_SIZE * GROUPS_PER_CHAIN;
    const chains = Math.ceil(VISIBLE / perChain);
    let idx = 0;
    for (let c = 0; c < chains; c++) {
      const chain = document.createElement("div");
      chain.className = "pal-chain";
      for (let g = 0; g < GROUPS_PER_CHAIN && idx < VISIBLE; g++) {
        if (g > 0) chain.append(band("pal-band-v"));
        const group = document.createElement("div");
        group.className = "pal-group";
        for (let k = 0; k < GROUP_SIZE && idx < VISIBLE; k++) {
          group.append(this.renderTile(idx));
          idx++;
        }
        chain.append(group);
      }
      canvas.append(chain);
      if (idx < VISIBLE) canvas.append(band("pal-band-h"));
    }
    this.container.append(canvas);
  }

  // Grab-to-pan: dragging anywhere that ISN'T a chip (i.e. the band/gaps) pans
  // the canvas in both axes. Chips keep their own tap/drag. Wired once on the
  // viewport, which survives re-renders.
  private setupPan(): void {
    const vp = this.container;
    let panning = false;
    let pid = -1;
    let sx = 0, sy = 0, sl = 0, st = 0;
    vp.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement).closest(".pal-tile")) return; // chip handles itself
      panning = true;
      pid = e.pointerId;
      sx = e.clientX; sy = e.clientY;
      sl = vp.scrollLeft; st = vp.scrollTop;
      vp.setPointerCapture(e.pointerId);
      vp.classList.add("panning");
    });
    vp.addEventListener("pointermove", (e) => {
      if (!panning || e.pointerId !== pid) return;
      vp.scrollLeft = sl - (e.clientX - sx);
      vp.scrollTop = st - (e.clientY - sy);
    });
    const end = (e: PointerEvent): void => {
      if (e.pointerId !== pid) return;
      panning = false;
      vp.classList.remove("panning");
    };
    vp.addEventListener("pointerup", end);
    vp.addEventListener("pointercancel", end);
  }

  private persist(): void {
    savePalette(this.palette);
  }

  private select(slot: PaletteSlot): void {
    this.selectedId = slot.id;
    this.opts.onPreview(slot);
    this.render();
  }

  private renderTile(index: number): HTMLElement {
    const slot = this.palette[index];
    const tile = document.createElement("div");
    tile.className = "pal-tile " + (slot ? `filled kind-${slot.kind}` : "empty");
    if (slot && slot.kind === "patch" && slot.patch.xfadeTo) tile.classList.add("crossfade");

    // Every tile shows its slot number 01..32 in the corner.
    const num = document.createElement("span");
    num.className = "tile-num";
    num.textContent = String(index + 1).padStart(2, "0");
    tile.append(num);

    if (slot) {
      // Coral marks the "special" chips — factory prefabs and merged crossfades;
      // plain saved sounds stay brand blue.
      const coral = slot.kind === "patch" && (slot.preset || !!slot.patch.xfadeTo);
      tile.style.background = coral ? "#ff7e6b" : "#00bfff";
      tile.title = slot.kind === "phrase"
        ? `${slot.name} (phrase)`
        : slot.patch.xfadeTo ? `${slot.name} (crossfade)` : slot.name;
      if (slot.id === this.selectedId) tile.classList.add("selected");
      makeDraggable(tile, () => ({ kind: "slot", id: String(index), label: slot.name }));

      // Body content depends on what's inside the cell:
      //   patch     → solid fill + short name
      //   phrase    → 2×2 grid of mini squares (the "blob" indicator)
      //   crossfade → 2 horizontal squares (A→B indicator)
      const body = document.createElement("div");
      body.className = "tile-body";
      if (slot.kind === "phrase") {
        body.classList.add("tile-grid", "grid-4");
        for (let i = 0; i < 4; i++) body.append(document.createElement("span"));
      } else if (slot.patch.xfadeTo) {
        body.classList.add("tile-grid", "grid-2");
        for (let i = 0; i < 2; i++) body.append(document.createElement("span"));
      } else {
        body.classList.add("tile-name");
        body.textContent = shortLabel(slot.name);
      }
      tile.append(body);

      tile.addEventListener("click", () => this.select(slot));
      tile.addEventListener("dblclick", () => this.opts.onLoad(slot.patch));

      const del = document.createElement("button");
      del.className = "pal-del";
      del.textContent = "✕";
      del.title = "Delete";
      del.addEventListener("click", (e) => {
        e.stopPropagation();
        this.opts.beforeChange?.(`Delete "${slot.name}"`);
        this.palette[index] = null;
        if (this.selectedId === slot.id) this.selectedId = null;
        this.persist();
        this.render();
      });
      tile.append(del);
    } else {
      // Empty slots: just the number, larger and centred.
      num.classList.add("tile-num-large");
      tile.title = "Save current sound here";
      tile.addEventListener("click", () => {
        const { patch, lastMidi } = this.opts.captureCurrent();
        const cloned = clonePatch(patch);
        const newSlot: PatchSlot = {
          id: genId(),
          kind: "patch",
          name: cloned.name,
          color: colorFor(cloned),
          patch: cloned,
          lastMidi,
        };
        this.palette[index] = newSlot;
        this.selectedId = newSlot.id;
        this.persist();
        this.render();
      });
    }

    // Any tile is a drop target for moving/swapping (or sound-on-sound crossfade).
    this.zoneCleanups.push(
      registerDropZone({
        el: tile,
        overClass: "dragover",
        accepts: (d) => d.kind === "slot",
        onDrop: (d) => this.handleSlotDrop(Number(d.id), index),
      }),
    );

    return tile;
  }

  // A palette tile was dropped onto another slot. Two patch tiles → crossfade
  // A→B (fade length = the note's duration wherever it ends up playing);
  // otherwise move/swap the tiles.
  private handleSlotDrop(src: number, dest: number): void {
    if (Number.isNaN(src) || src === dest) return;
    const a = this.palette[src];
    const b = this.palette[dest];
    // A crossfade overwrites the destination tile outright, so it needs an
    // undo point just as much as an explicit delete does.
    if (a && b && a.kind === "patch" && b.kind === "patch") {
      this.opts.beforeChange?.(`Merge "${a.name}" → "${b.name}"`);
      const blended: Patch = {
        ...clonePatch(a.patch),
        xfadeTo: clonePatch(b.patch),
        name: `${a.patch.name}→${b.patch.name}`.slice(0, 18),
      };
      const newSlot: PatchSlot = {
        id: genId(),
        kind: "patch",
        name: blended.name,
        color: colorFor(blended),
        patch: blended,
        lastMidi: a.lastMidi,
      };
      this.palette[dest] = newSlot;
      this.selectedId = newSlot.id;
      this.persist();
      this.render();
      return;
    }
    const tmp = this.palette[dest];
    this.palette[dest] = this.palette[src];
    this.palette[src] = tmp;
    this.persist();
    this.render();
  }

  /** Add a slot (e.g. a recorded phrase) into the first empty cell of the strip. */
  addSlot(slot: PaletteSlot): void {
    let i = -1;
    for (let k = 0; k < VISIBLE; k++) {
      if (!this.palette[k]) {
        i = k;
        break;
      }
    }
    if (i < 0) i = this.palette.findIndex((s) => s === null);
    if (i < 0) i = this.palette.length - 1;
    this.palette[i] = slot;
    this.selectedId = slot.id;
    this.persist();
    this.render();
  }
}
